"use client"

import {
  useCallback,
  useEffect,
  useRef,
  type FormEventHandler,
  type ReactNode,
} from "react"
import CircularProgress from "@mui/material/CircularProgress"
import { useTheme } from "@mui/material/styles"
import SectionTitleDecoration from "../../icons/SectionTitleDecoration"
import { useOneShotEntrance } from "../../transitions/useOneShotEntrance"
import { useIsDesktop } from "../bit/MediaQueries"
import type { ShellLabels } from "./baseSchema"
import {
  StyledRoot,
  StyledMapBg,
  StyledContentWrapper,
  StyledDecorative,
  StyledXArmStart,
  StyledXArmEnd,
  StyledFormSection,
  StyledTitleRow,
  StyledTitleDecoration,
  StyledTitle,
  StyledSubtitle,
  StyledFormCard,
  StyledFormHeader,
  StyledRequiredNote,
  StyledRequired,
  StyledFieldsContainer,
  StyledFooter,
  StyledSubmitButton,
  StyledSubmitText,
  StyledStateCard,
  StyledStateIcon,
  StyledStateTitle,
  StyledStateMessage,
  StyledRetryButton,
  StyledRetryText,
} from "./FormShell.styles"

export type FormSubmitState = "idle" | "success" | "error"

interface FormShellProps {
  title?: string | null
  subtitle?: string | null
  labels: ShellLabels
  state: FormSubmitState
  isPending: boolean
  onSubmit: FormEventHandler<HTMLFormElement>
  onRetry: () => void
  /** Renders the map background + decorative X graphic on desktop. */
  showDecorative?: boolean
  /** Checkbox row (and any other footer content) above the submit button. */
  footer?: ReactNode
  /** The form fields. */
  children: ReactNode
}

/* EGDE-448 (QA return): the Figma success mark is a SOLID chunky checkmark,
   not a stroked line — the previous 12px round-capped stroke read as thin
   against the design. Drawn as a filled polygon with the brand CTA gradient;
   the same-gradient 6px round-joined stroke softens the corners like the
   design's slightly-rounded solid mark. */
const SuccessIcon = () => (
  <svg
    width="88"
    height="88"
    viewBox="0 0 88 88"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient
        id="formSuccessCheck"
        x1="11"
        y1="66"
        x2="80"
        y2="14"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F86F3E" />
        <stop offset="1" stopColor="#FFB36E" />
      </linearGradient>
    </defs>
    <path
      d="M18.5 33.5L33.5 48.5L69.5 12.5L80 23L33.5 69.5L8 44Z"
      fill="url(#formSuccessCheck)"
      stroke="url(#formSuccessCheck)"
      strokeWidth="6"
      strokeLinejoin="round"
    />
  </svg>
)

const ErrorIcon = () => (
  /* EGDE-228: 88px to fill the shared 88px icon box, matching the success
     check's size (the box grew with the success icon). */
  <svg
    width="88"
    height="88"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="32" cy="32" r="28" fill="#F86F3E" />
    <path d="M32 20V36" stroke="white" strokeWidth="4" strokeLinecap="round" />
    <circle cx="32" cy="44" r="2.5" fill="white" />
  </svg>
)

/**
 * Shared chrome for every Egged Plus form block: section layout (title /
 * subtitle / card / decorative) plus the idle → success / error state machine
 * and the submit button. Each form supplies only its fields (children), its
 * checkbox (footer), and the localized labels.
 */
export default function FormShell({
  title,
  subtitle,
  labels,
  state,
  isPending,
  onSubmit,
  onRetry,
  showDecorative = true,
  footer,
  children,
}: FormShellProps) {
  // EGDE-318: the brand X forms as it enters view — on desktop its two arms
  // grow from their own centres and interlock (see StyledXArm*); on mobile the
  // whole X scales+fades. Always on: per the ticket only contentCard /
  // logosStrip / powerNumbers carry a CMS toggle; all forms share this default
  // entrance.
  //
  // EGDE-510 r4 (Raz, 2026-07-29): this used to be a useScrollScrub, which
  // maps scroll POSITION to progress — so the form column kept sliding for as
  // long as the visitor kept scrolling and ran backwards on the way up. Raz
  // read that as the fault "note that the entire section shifts to the right …
  // no parallax scroll animation with the mouse was defined here. Only the
  // information entrance as in the video I attached in the ticket body."
  // It is now a ONE-SHOT: the designed beat plays once when the section
  // arrives and the section never moves again. The prototype's travel
  // direction is unchanged (183220: the copy starts inline-end of its seat and
  // settles rightwards in RTL) — only the parallax coupling is gone.
  const isDesktop = useIsDesktop()
  const decorativeRef = useRef<HTMLDivElement>(null)
  // --scrub lives on the content WRAPPER, not the decorative box: the form
  // column's entrance consumes the same progress and custom properties only
  // inherit downward.
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Observed on the wrapper rather than the map box so the entrance also runs
  // for the forms that render without the decorative (showDecorative=false)
  // and on mobile, where the box is laid out differently. EGDE-510 point A2:
  // mobile had NO motion at all here (measured 0 movers over a full sweep).
  useOneShotEntrance(wrapperRef, {
    duration: isDesktop ? 900 : 750,
    onProgress: useCallback((p: number) => {
      wrapperRef.current?.style.setProperty("--scrub", String(p))
    }, []),
  })
  // EGDE-486: the brand X must stay centred on the FORM column, not the map
  // box — in Figma both are centred in the 800px row so their centres
  // coincide, but our form is top-aligned (EGDE-339), leaving a box-centred
  // logo visibly lower. Measure the form column (ResizeObserver tracks
  // growing textareas, validation errors and the success-card swap) and
  // publish its centre — in the map box's coordinate space, clamped so the
  // 393px artwork never crosses the box's clipped edges — as --form-center.
  const formSectionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!showDecorative) return
    const section = formSectionRef.current
    const decorative = decorativeRef.current
    if (!section || !decorative || typeof ResizeObserver === "undefined") return
    const update = () => {
      // Both elements resolve offsetTop against the positioned wrapper.
      const center =
        section.offsetTop + section.offsetHeight / 2 - decorative.offsetTop
      const halfArm = 196.5 // 393px artwork (xArmBase) / 2
      const clamped = Math.min(
        Math.max(center, halfArm),
        Math.max(decorative.offsetHeight - halfArm, halfArm),
      )
      decorative.style.setProperty("--form-center", `${clamped}px`)
    }
    const observer = new ResizeObserver(update)
    observer.observe(section)
    observer.observe(decorative)
    update()
    return () => observer.disconnect()
  }, [showDecorative, state])

  /* EGDE-240: on mobile the form is much taller than the success/error card,
     so when the card replaces it the document collapses and the browser keeps a
     scroll offset that now lands on the footer. Bring the result card back into
     view. Gated on visibility so desktop — where the card is already on-screen —
     never scrolls (no regression). */
  const stateCardRef = useRef<HTMLDivElement>(null)
  // EGDE-289: mirror the RTL-drawn section chevron on LTR pages.
  const isRtl = useTheme().direction === "rtl"
  useEffect(() => {
    if (state !== "success" && state !== "error") return
    const el = stateCardRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight
    if (!fullyVisible) {
      el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [state])

  const titleBlock = title ? (
    <StyledTitleRow>
      <StyledTitleDecoration>
        <SectionTitleDecoration
          style={isRtl ? undefined : { transform: "scaleX(-1)" }}
        />
      </StyledTitleDecoration>
      <StyledTitle variant="h2">{title}</StyledTitle>
    </StyledTitleRow>
  ) : null

  if (state === "success" || state === "error") {
    const isSuccess = state === "success"
    return (
      <StyledRoot>
        {showDecorative && <StyledMapBg />}
        <StyledContentWrapper ref={wrapperRef}>
          {showDecorative && (
            <StyledDecorative ref={decorativeRef}>
              <StyledXArmStart />
              <StyledXArmEnd />
            </StyledDecorative>
          )}
          <StyledFormSection ref={formSectionRef}>
            {/* EGDE-663: once the form is actually sent, the section title has
                nothing left to introduce — QA: "after sending the form, the
                title will disappear" — so the thank-you card stands alone. The
                ERROR state keeps it: the visitor is still looking at a form
                they have to resubmit via Retry, and the title is what says
                which form that is. */}
            {!isSuccess && titleBlock}
            <StyledStateCard ref={stateCardRef}>
              <StyledStateIcon>
                {isSuccess ? <SuccessIcon /> : <ErrorIcon />}
              </StyledStateIcon>
              <StyledStateTitle variant="h2">
                {isSuccess ? labels.successTitle : labels.errorTitle}
              </StyledStateTitle>
              <StyledStateMessage variant="body1">
                {isSuccess ? labels.successMessage : labels.errorMessage}
              </StyledStateMessage>
              {!isSuccess && (
                <StyledRetryButton type="button" onClick={onRetry}>
                  <StyledRetryText variant="body1">
                    {labels.retry}
                  </StyledRetryText>
                </StyledRetryButton>
              )}
            </StyledStateCard>
          </StyledFormSection>
        </StyledContentWrapper>
      </StyledRoot>
    )
  }

  return (
    <StyledRoot>
      {showDecorative && <StyledMapBg />}
      <StyledContentWrapper ref={wrapperRef}>
        {showDecorative && (
          <StyledDecorative ref={decorativeRef}>
            <StyledXArmStart />
            <StyledXArmEnd />
          </StyledDecorative>
        )}
        <StyledFormSection ref={formSectionRef}>
          {titleBlock}

          {subtitle && (
            <StyledSubtitle variant="body1">{subtitle}</StyledSubtitle>
          )}

          <form onSubmit={onSubmit} noValidate style={{ width: "100%" }}>
            <StyledFormCard>
              <StyledFormHeader>
                <StyledRequiredNote>
                  {labels.requiredFields}
                  <StyledRequired>*</StyledRequired>
                </StyledRequiredNote>
              </StyledFormHeader>

              <StyledFieldsContainer>{children}</StyledFieldsContainer>

              <StyledFooter>
                {footer}

                <StyledSubmitButton type="submit" disabled={isPending}>
                  {isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    <StyledSubmitText variant="body1">
                      {labels.submit}
                    </StyledSubmitText>
                  )}
                </StyledSubmitButton>
              </StyledFooter>
            </StyledFormCard>
          </form>
        </StyledFormSection>
      </StyledContentWrapper>
    </StyledRoot>
  )
}
